<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'

const props = defineProps<{
  value: string
  label: string
  suffix?: string
}>()

const numeric = computed(() => {
  const match = props.value.match(/[\d.]+/)
  return match ? parseFloat(match[0]) : 0
})

const suffixValue = computed(() => {
  return props.value.replace(/[\d.]+/, '')
})

const display = ref('0')
const rootRef = ref<HTMLElement | null>(null)
let observer: IntersectionObserver | null = null
let rafId: number | null = null

function animate(target: number, duration = 1800) {
  const start = performance.now()
  const isFloat = !Number.isInteger(target)

  const step = (now: number) => {
    const progress = Math.min((now - start) / duration, 1)
    const eased = 1 - Math.pow(1 - progress, 4)
    const current = target * eased
    display.value = isFloat ? current.toFixed(1) : Math.floor(current).toString()
    if (progress < 1) {
      rafId = requestAnimationFrame(step)
    }
  }

  rafId = requestAnimationFrame(step)
}

onMounted(() => {
  if (!rootRef.value || !window.IntersectionObserver) {
    display.value = numeric.value.toString()
    return
  }
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          animate(numeric.value)
          observer?.unobserve(entry.target)
        }
      })
    },
    { threshold: 0.3 }
  )
  observer.observe(rootRef.value)
})

onUnmounted(() => {
  if (observer) observer.disconnect()
  if (rafId) cancelAnimationFrame(rafId)
})
</script>

<template>
  <div ref="rootRef" class="kpi-counter">
    <div class="kpi-value mono">
      <span class="kpi-number">{{ display }}</span>
      <span class="kpi-suffix">{{ suffixValue }}{{ suffix || '' }}</span>
    </div>
    <div class="kpi-label">{{ label }}</div>
  </div>
</template>

<style scoped>
.kpi-counter {
  text-align: center;
  padding: var(--space-6);
}

.kpi-value {
  font-size: clamp(36px, 5vw, 56px);
  font-weight: 700;
  line-height: 1.1;
  color: var(--u2-text);
}

.kpi-suffix {
  color: var(--u2-primary);
}

.kpi-label {
  margin-top: var(--space-3);
  color: var(--u2-muted);
  font-size: 14px;
}
</style>
